/**
 * Global Theme - Single Source of Truth for all design tokens
 * Import this file for all styling needs across the app
 */

// ============================================================================
// COLORS
// ============================================================================

export const colors = {
  // Backgrounds
  background: {
    primary: '#0a0a0a',
    secondary: '#141414',
    tertiary: '#1f1f1f',
    elevated: '#2a2a2a',
  },

  // Surfaces (overlays, cards)
  surface: {
    primary: 'rgba(255, 255, 255, 0.05)',
    secondary: 'rgba(255, 255, 255, 0.08)',
    hover: 'rgba(255, 255, 255, 0.12)',
  },

  // Text
  text: {
    primary: 'rgba(255, 255, 255, 0.95)',
    secondary: 'rgba(255, 255, 255, 0.75)',
    tertiary: 'rgba(255, 255, 255, 0.55)',
    disabled: 'rgba(255, 255, 255, 0.35)',
  },

  // Borders
  border: {
    primary: 'rgba(255, 255, 255, 0.12)',
    secondary: 'rgba(255, 255, 255, 0.08)',
    focus: 'rgba(255, 255, 255, 0.25)',
  },

  // Brand
  brand: {
    primary: '#667eea',
    primaryHover: '#7c94f5',
    secondary: '#f093fb',
    secondaryHover: '#f5a7fc',
    accent: '#FA8B14',
  },

  // Status
  status: {
    success: '#52c41a',
    warning: '#fa8c16',
    error: '#f5576c',
    info: '#1890ff',
  },

  // Gradient colors
  gradient: {
    from: '#667eea',
    via: '#764ba2',
    to: '#f093fb',
  },
};

// ============================================================================
// SPACING (in pixels)
// ============================================================================

export const spacing = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 20,
  xl: 24,
  xxl: 32,
  xxxl: 48,
};

// ============================================================================
// BORDER RADIUS (in pixels)
// ============================================================================

export const radius = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  full: 9999,
};

// ============================================================================
// TYPOGRAPHY
// ============================================================================

export const typography = {
  fontSize: {
    xs: 11,
    sm: 12,
    md: 14,
    lg: 16,
    xl: 18,
    xxl: 24,
    xxxl: 32,
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
};

// ============================================================================
// SHADOWS
// ============================================================================

export const shadows = {
  sm: '0 2px 8px rgba(0, 0, 0, 0.15)',
  md: '0 4px 16px rgba(0, 0, 0, 0.2)',
  lg: '0 8px 32px rgba(0, 0, 0, 0.25)',
  xl: '0 12px 48px rgba(0, 0, 0, 0.3)',
};

// ============================================================================
// TRANSITIONS
// ============================================================================

export const transitions = {
  fast: '0.15s ease',
  normal: '0.2s ease',
  slow: '0.3s ease',
  timing: 'cubic-bezier(0.4, 0, 0.2, 1)',
};

// ============================================================================
// GRADIENTS (CSS strings)
// ============================================================================

export const gradients = {
  primary: `linear-gradient(135deg, ${colors.gradient.from} 0%, ${colors.gradient.via} 50%, ${colors.gradient.to} 100%)`,
  secondary: `linear-gradient(135deg, ${colors.brand.secondary} 0%, ${colors.status.error} 100%)`,
  success: `linear-gradient(135deg, ${colors.status.success} 0%, #3da00d 100%)`,
  accent: `linear-gradient(135deg, ${colors.brand.accent} 0%, #e67300 100%)`,
};

// ============================================================================
// Z-INDEX LAYERS
// ============================================================================

export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  fixed: 300,
  modalBackdrop: 400,
  modal: 500,
  popover: 600,
  tooltip: 700,
  toast: 800,
};

// ============================================================================
// BREAKPOINTS (in pixels)
// ============================================================================

export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
};

// ============================================================================
// UNIFIED THEME OBJECT (for backward compatibility)
// ============================================================================

export const theme = {
  colors,
  spacing,
  radius,
  typography,
  shadows,
  transitions,
  gradients,
  zIndex,
  breakpoints,
};

// Default export
export default theme;
